import { generateFieldExamples, generateFieldTypeGuide } from '@/lib/field-type-intelligence';
import { queryOpenRouter } from '@/lib/openrouter';
import { requestSystemSettingByKey } from '@/graphql/queries/admin';
import { parseJsonResponse } from '@/modules/templates/services/ai/ai-utils';

// ============================================================
// Types
// ============================================================

export interface TemplateConfiguratorRequest {
  messages: { role: 'user' | 'assistant'; content: string }[];
  templateContext: {
    templateId: string;
    templateTitle: string;
    templateDescription?: string;
    screens: Array<{
      id: string;
      title: string;
      description?: string | null;
      order: number;
      type?: 'standard' | 'signatory' | 'dynamic';
      enableApplyStandards?: boolean;
      aiPrompt?: string | null;
      aiOutputSchema?: string | null;
      signatoryConfig?: string | null;
      dynamicPrompt?: string | null;
      dynamicMaxFields?: number | null;
      conditions?: string | null;
      fields: Array<{
        name: string;
        label: string;
        type: string;
        required: boolean;
        placeholder?: string | null;
        helpText?: string | null;
        options?: string[];
        aiSuggestionEnabled?: boolean;
        aiSuggestionKey?: string | null;
        conditions?: string | null;
      }>;
    }>;
  };
  selectedScreenId?: string;
}

export interface TemplateConfiguratorResponse {
  thinking?: string;
  message: string;
  suggestions?: string[];
  actions: Array<{
    type:
      | 'createScreen'
      | 'updateScreen'
      | 'addFields'
      | 'removeScreen'
      | 'reorderScreens';
    data: any;
  }>;
  autoNext?: boolean;
}

// ============================================================
// Constants
// ============================================================

const VALID_ACTION_TYPES = [
  'createScreen',
  'updateScreen',
  'addFields',
  'removeScreen',
  'reorderScreens',
] as const;

const FORM_SCHEMA_REFERENCE = `
## Form Schema Reference

### Screen Object Structure
A screen represents a step/page in the template form wizard.

\`\`\`typescript
interface Screen {
  id: string;                          // Unique identifier (auto-generated)
  title: string;                       // Display title for the screen/step
  description?: string;                // Optional description shown below title
  order: number;                       // Display order (0-based)
  type?: 'standard' | 'signatory' | 'dynamic';  // Screen type
  enableApplyStandards?: boolean;      // Whether to show "Apply Standards" button
  aiPrompt?: string;                   // AI prompt for auto-filling fields
  aiOutputSchema?: string;             // JSON schema for AI output structure
  signatoryConfig?: string;            // JSON config for signatory screens
  dynamicPrompt?: string;              // Prompt for dynamic field generation
  dynamicMaxFields?: number;           // Max fields for dynamic screens
  conditions?: string;                 // JSON conditions for screen visibility
  fields: Field[];                     // Array of form fields
}
\`\`\`

### Field Object Structure
\`\`\`typescript
interface Field {
  name: string;           // Unique field identifier (snake_case recommended)
  label: string;          // Display label
  type: string;           // Field type (see below)
  required: boolean;      // Whether the field is mandatory
  placeholder?: string;   // Placeholder text
  helpText?: string;      // Help text shown below the field
  options?: string[];     // Options for select/radio/checkbox fields
  aiSuggestionEnabled?: boolean;  // Enable AI suggestions for this field
  aiSuggestionKey?: string;       // Key for AI suggestion mapping
  conditions?: string;            // JSON conditions for field visibility
}
\`\`\`

### Available Field Types
- \`text\` - Single line text input
- \`textarea\` - Multi-line text input
- \`number\` - Numeric input
- \`email\` - Email address input
- \`phone\` - Phone number input
- \`date\` - Date picker
- \`select\` - Dropdown selection (requires options)
- \`radio\` - Radio button group (requires options)
- \`checkbox\` - Checkbox (boolean)
- \`multi-select\` - Multiple selection (requires options)
- \`file\` - File upload
- \`currency\` - Currency amount input
- \`percentage\` - Percentage input
- \`url\` - URL input
- \`address\` - Address block (composite field)
- \`signature\` - Signature capture
- \`heading\` - Section heading (display only)
- \`paragraph\` - Paragraph text (display only)
- \`divider\` - Visual divider (display only)

### Screen Types
- \`standard\` - Regular form screen with static fields
- \`signatory\` - Special screen for collecting signatory/party information
- \`dynamic\` - Screen where fields are generated dynamically by AI based on context

### Conditions Format
Conditions control visibility of screens and fields based on other field values.
\`\`\`json
{
  "operator": "AND",
  "rules": [
    {
      "field": "field_name",
      "operator": "equals",
      "value": "some_value"
    }
  ]
}
\`\`\`
Available operators: \`equals\`, \`notEquals\`, \`contains\`, \`notContains\`, \`greaterThan\`, \`lessThan\`, \`isEmpty\`, \`isNotEmpty\`
Group operators: \`AND\`, \`OR\`
`;

const BASE_SYSTEM_PROMPT = `You are an expert legal template configurator AI assistant. Your role is to help users design and configure legal document templates through a conversational interface.

## Your Capabilities
You can help users:
1. **Create new screens** (steps/pages) in a template
2. **Update existing screens** (modify title, description, type, settings)
3. **Add fields** to screens (form inputs that collect data)
4. **Remove screens** from the template
5. **Reorder screens** to change the template flow

## Response Format
You MUST always respond with a valid JSON object in this exact format:
\`\`\`json
{
  "thinking": "Your internal reasoning about what the user wants (optional)",
  "message": "Your conversational response to the user explaining what you did or suggesting next steps",
  "suggestions": ["Suggestion 1", "Suggestion 2", "Suggestion 3"],
  "actions": [
    {
      "type": "createScreen" | "updateScreen" | "addFields" | "removeScreen" | "reorderScreens",
      "data": { ... }
    }
  ],
  "autoNext": false
}
\`\`\`

## Action Data Structures

### createScreen
Creates a new screen with optional fields.
\`\`\`json
{
  "type": "createScreen",
  "data": {
    "title": "Screen Title",
    "description": "Optional description",
    "order": 0,
    "screenType": "standard",
    "enableApplyStandards": false,
    "aiPrompt": null,
    "aiOutputSchema": null,
    "fields": [
      {
        "name": "field_name",
        "label": "Field Label",
        "type": "text",
        "required": true,
        "placeholder": "Enter value...",
        "helpText": "Help text for the user",
        "options": [],
        "aiSuggestionEnabled": false,
        "aiSuggestionKey": null,
        "conditions": null
      }
    ]
  }
}
\`\`\`

### updateScreen
Updates an existing screen's properties.
\`\`\`json
{
  "type": "updateScreen",
  "data": {
    "screenId": "existing-screen-id",
    "title": "Updated Title",
    "description": "Updated description",
    "screenType": "standard",
    "enableApplyStandards": true,
    "aiPrompt": "Updated prompt",
    "aiOutputSchema": "{}",
    "conditions": null
  }
}
\`\`\`

### addFields
Adds new fields to an existing screen.
\`\`\`json
{
  "type": "addFields",
  "data": {
    "screenId": "existing-screen-id",
    "fields": [
      {
        "name": "new_field",
        "label": "New Field",
        "type": "text",
        "required": false,
        "placeholder": "",
        "helpText": "",
        "options": [],
        "aiSuggestionEnabled": false,
        "aiSuggestionKey": null,
        "conditions": null
      }
    ]
  }
}
\`\`\`

### removeScreen
Removes a screen from the template.
\`\`\`json
{
  "type": "removeScreen",
  "data": {
    "screenId": "screen-id-to-remove"
  }
}
\`\`\`

### reorderScreens
Changes the order of screens.
\`\`\`json
{
  "type": "reorderScreens",
  "data": {
    "screenOrder": [
      { "screenId": "screen-1", "order": 0 },
      { "screenId": "screen-2", "order": 1 },
      { "screenId": "screen-3", "order": 2 }
    ]
  }
}
\`\`\`

## Important Rules

1. **Always respond with valid JSON** - Never include text outside the JSON object
2. **Use meaningful field names** - Use snake_case for field names (e.g., \`party_name\`, \`contract_start_date\`)
3. **Set appropriate field types** - Choose the most specific field type (e.g., \`email\` for emails, \`date\` for dates, \`currency\` for money amounts)
4. **Include helpful placeholders and help text** - Guide users on what to enter
5. **Mark truly required fields** - Only mark fields as required if they are essential
6. **Consider the template flow** - Suggest logical screen ordering
7. **Be conversational** - Explain what you're doing and suggest next steps
8. **Provide suggestions** - Always include 2-4 relevant follow-up suggestions
9. **Reference existing screens** - When updating or adding fields, use the correct screen IDs from the template context
10. **Handle ambiguity** - If the user's request is unclear, ask for clarification instead of guessing
11. **Legal domain expertise** - Apply legal document best practices (proper party identification, jurisdiction selection, date formatting, signature blocks, etc.)
12. **Screen types** - Use \`signatory\` type for screens collecting party/signatory info, \`dynamic\` for AI-generated fields, \`standard\` for everything else

## Enrichment Variables
When configuring AI prompts for screens, you can reference these enrichment variables that are populated from previous screens:
- Use \`{{fieldName}}\` syntax to reference field values
- Variables are available from all screens that come before the current screen in order
- Common patterns: \`{{party_name}}\`, \`{{jurisdiction}}\`, \`{{contract_type}}\`

## Best Practices for Legal Templates
1. **Start with party identification** - Who are the parties involved?
2. **Collect key terms early** - Contract type, jurisdiction, effective date
3. **Group related fields** - Keep related information on the same screen
4. **Use conditional visibility** - Show/hide fields based on previous answers
5. **End with review and signatures** - Final screen should be signatory type
6. **AI-powered screens** - Use dynamic screens for complex clause generation
7. **Standards compliance** - Enable "Apply Standards" for screens that should follow regulatory requirements
`;

const DEFAULT_BUSINESS_LOGIC_PROMPT = `
## Additional Business Logic

When creating legal templates, follow these guidelines:
1. Always include a parties/signatories screen as one of the first screens
2. Include jurisdiction and governing law selection
3. Group related clauses together
4. Use conditional fields when certain options depend on previous selections
5. Include a review/summary screen before the final signature screen
6. For employment contracts: include compensation, benefits, termination clauses
7. For NDAs: include definition of confidential information, exclusions, term/duration
8. For service agreements: include scope of work, payment terms, liability limitations
9. For lease agreements: include property details, rent terms, maintenance responsibilities
`;

// ============================================================
// Helpers
// ============================================================

function buildTemplateContextPrompt(request: TemplateConfiguratorRequest): string {
  const { templateContext, selectedScreenId } = request;
  const { templateId, templateTitle, templateDescription, screens } = templateContext;

  let contextPrompt = `
## Current Template Context
- **Template ID**: ${templateId}
- **Template Title**: ${templateTitle}
${templateDescription ? `- **Description**: ${templateDescription}` : ''}
- **Total Screens**: ${screens.length}
`;

  if (screens.length > 0) {
    contextPrompt += `
### Existing Screens
`;
    for (const screen of screens) {
      contextPrompt += `
#### Screen: "${screen.title}" (ID: ${screen.id})
- **Order**: ${screen.order}
- **Type**: ${screen.type || 'standard'}
${screen.description ? `- **Description**: ${screen.description}` : ''}
${screen.enableApplyStandards ? '- **Apply Standards**: Enabled' : ''}
${screen.aiPrompt ? `- **AI Prompt**: ${screen.aiPrompt}` : ''}
${screen.conditions ? `- **Conditions**: ${screen.conditions}` : ''}
- **Fields** (${screen.fields.length}):
`;
      for (const field of screen.fields) {
        contextPrompt += `  - \`${field.name}\` (${field.type}${field.required ? ', required' : ''}): "${field.label}"`;
        if (field.options && field.options.length > 0) {
          contextPrompt += ` [options: ${field.options.join(', ')}]`;
        }
        if (field.helpText) {
          contextPrompt += ` - Help: "${field.helpText}"`;
        }
        if (field.conditions) {
          contextPrompt += ` - Conditions: ${field.conditions}`;
        }
        contextPrompt += '\n';
      }
    }
  } else {
    contextPrompt += `
### No screens exist yet
This is a blank template. You should suggest creating the first screen(s).
`;
  }

  // Available enrichment variables from all screens
  const allFields = screens.flatMap((s) => s.fields);
  if (allFields.length > 0) {
    contextPrompt += `
### Available Enrichment Variables
These variables can be used in AI prompts with \`{{variableName}}\` syntax:
`;
    for (const field of allFields) {
      contextPrompt += `- \`{{${field.name}}}\` - ${field.label} (${field.type})\n`;
    }
  }

  // Selected screen info
  if (selectedScreenId) {
    const selectedScreen = screens.find((s) => s.id === selectedScreenId);
    if (selectedScreen) {
      contextPrompt += `
### Currently Selected Screen
The user is currently viewing/editing: **"${selectedScreen.title}"** (ID: ${selectedScreen.id})
When the user says "this screen" or "here", they are referring to this screen.
`;
    }
  }

  return contextPrompt;
}

function buildSystemPrompt(
  request: TemplateConfiguratorRequest,
  businessLogicPrompt: string
): string {
  const templateContextPrompt = buildTemplateContextPrompt(request);

  return [
    BASE_SYSTEM_PROMPT,
    FORM_SCHEMA_REFERENCE,
    generateFieldTypeGuide(),
    generateFieldExamples(),
    templateContextPrompt,
    businessLogicPrompt,
  ].join('\n\n');
}

type ValidatedAction = TemplateConfiguratorResponse['actions'][number];

function validateActions(
  actions: unknown[]
): TemplateConfiguratorResponse['actions'] {
  if (!Array.isArray(actions)) return [];

  const validated: ValidatedAction[] = [];

  for (const action of actions) {
    if (!action || typeof action !== 'object') continue;
    const a = action as Record<string, unknown>;
    if (
      typeof a.type !== 'string' ||
      !VALID_ACTION_TYPES.includes(a.type as (typeof VALID_ACTION_TYPES)[number])
    ) {
      continue;
    }
    if (!a.data || typeof a.data !== 'object') continue;

    const actionType = a.type as ValidatedAction['type'];
    const d = a.data as Record<string, unknown>;

    switch (actionType) {
      case 'createScreen': {
        validated.push({
          type: actionType,
          data: {
            title: typeof d.title === 'string' ? d.title : 'Untitled Screen',
            description:
              typeof d.description === 'string' ? d.description : undefined,
            order: typeof d.order === 'number' ? d.order : undefined,
            screenType:
              typeof d.screenType === 'string' ? d.screenType : 'standard',
            enableApplyStandards: Boolean(d.enableApplyStandards),
            aiPrompt:
              typeof d.aiPrompt === 'string' ? d.aiPrompt : undefined,
            aiOutputSchema:
              typeof d.aiOutputSchema === 'string'
                ? d.aiOutputSchema
                : undefined,
            signatoryConfig:
              typeof d.signatoryConfig === 'string'
                ? d.signatoryConfig
                : undefined,
            dynamicPrompt:
              typeof d.dynamicPrompt === 'string'
                ? d.dynamicPrompt
                : undefined,
            dynamicMaxFields:
              typeof d.dynamicMaxFields === 'number'
                ? d.dynamicMaxFields
                : undefined,
            conditions:
              typeof d.conditions === 'string' ? d.conditions : undefined,
            fields: Array.isArray(d.fields) ? d.fields : [],
          },
        });
        break;
      }
      case 'updateScreen': {
        if (typeof d.screenId !== 'string') continue;
        validated.push({
          type: actionType,
          data: {
            screenId: d.screenId,
            ...(typeof d.title === 'string' && { title: d.title }),
            ...(typeof d.description === 'string' && {
              description: d.description,
            }),
            ...(typeof d.screenType === 'string' && {
              screenType: d.screenType,
            }),
            ...(typeof d.enableApplyStandards === 'boolean' && {
              enableApplyStandards: d.enableApplyStandards,
            }),
            ...(typeof d.aiPrompt === 'string' && { aiPrompt: d.aiPrompt }),
            ...(typeof d.aiOutputSchema === 'string' && {
              aiOutputSchema: d.aiOutputSchema,
            }),
            ...(typeof d.conditions === 'string' && {
              conditions: d.conditions,
            }),
          },
        });
        break;
      }
      case 'addFields': {
        if (typeof d.screenId !== 'string' || !Array.isArray(d.fields)) {
          continue;
        }
        validated.push({
          type: actionType,
          data: {
            screenId: d.screenId,
            fields: d.fields,
          },
        });
        break;
      }
      case 'removeScreen': {
        if (typeof d.screenId !== 'string') continue;
        validated.push({
          type: actionType,
          data: { screenId: d.screenId },
        });
        break;
      }
      case 'reorderScreens': {
        if (!Array.isArray(d.screenOrder)) continue;
        validated.push({
          type: actionType,
          data: { screenOrder: d.screenOrder },
        });
        break;
      }
    }
  }

  return validated;
}

// ============================================================
// Main Service Function
// ============================================================

export async function callTemplateConfigurator(
  request: TemplateConfiguratorRequest
): Promise<TemplateConfiguratorResponse> {
  // 1. Validate request
  if (!request.messages || request.messages.length === 0) {
    throw new Error('Messages are required');
  }
  if (!request.templateContext) {
    throw new Error('Template context is required');
  }

  // 2. Fetch settings from UDS
  const [modelSetting, businessLogicSetting] = await Promise.all([
    requestSystemSettingByKey('templateConfiguratorAiModel'),
    requestSystemSettingByKey('templateConfiguratorBusinessLogic'),
  ]);

  const aiModel = modelSetting?.Value || 'anthropic/claude-sonnet-4';
  const businessLogicPrompt =
    businessLogicSetting?.Value || DEFAULT_BUSINESS_LOGIC_PROMPT;

  // 3. Build system prompt
  const systemPrompt = buildSystemPrompt(request, businessLogicPrompt);

  // 4. Call OpenRouter
  const result = await queryOpenRouter({
    messages: [
      { role: 'system', content: systemPrompt },
      ...request.messages,
    ],
    model: aiModel,
    responseFormat: 'json',
    temperature: 0.7,
    maxTokens: 8192,
  });

  // 5. Parse JSON response with fallback strategies
  let parsed: Record<string, unknown>;
  try {
    parsed = parseJsonResponse(result.message);
  } catch {
    // Final fallback: wrap as plain text message
    return {
      message:
        result.message ||
        'I received your request but had trouble formatting my response. Could you try rephrasing?',
      suggestions: ['Try again', 'Start over', 'Help me with this template'],
      actions: [],
    };
  }

  // 6. Build and validate response
  const response: TemplateConfiguratorResponse = {
    thinking: typeof parsed.thinking === 'string' ? parsed.thinking : undefined,
    message:
      typeof parsed.message === 'string'
        ? parsed.message
        : 'I processed your request.',
    suggestions: Array.isArray(parsed.suggestions)
      ? (parsed.suggestions as string[]).filter(
          (s): s is string => typeof s === 'string'
        )
      : undefined,
    actions: validateActions(
      Array.isArray(parsed.actions) ? parsed.actions : []
    ),
    autoNext:
      typeof parsed.autoNext === 'boolean' ? parsed.autoNext : undefined,
  };

  return response;
}
